import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpResponse
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable, of, OperatorFunction, throwError } from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  map,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import {
  CoreHttpActionDecrementPendingQueue,
  CoreHttpActionIncrementPendingQueue,
  CoreHttpActionApiResponseError,
  CoreHttpActionInternetIsNotWorking,
  CoreHttpActionResponseInternalError,
  CoreHttpActionSetError
} from './core-http.actions';
import {
  BepatientHttpStatusTextsError,
  CoreHttpError,
  CoreHttpRequestOptions
} from './core-http.models';
import { CoreActionJwtHasExpired } from './core.actions';
import {
  selectorCoreInstanceInfo,
  selectorCoreLogin,
  selectorCoreSelectedInstance,
  selectorCoreInstancesData,
  selectorCoreInstanceEnv
} from './core.reducer';

/**
 * Service responsable de faire les calls HTTP
 */
@Injectable()
export class CoreHttpClient {
  private store = inject<Store<any>>(Store);
  private http = inject(HttpClient);

  /**
   * @ignore
   */
  private iAuth: string;
  /**
   * @ignore
   */
  private instanceUrl: string;

  /**
   * @ignore
   */
  constructor() {
    const store = this.store;

    store
      .pipe(
        select(selectorCoreLogin),
        filter(login => !!login)
      )
      .subscribe(login => (this.iAuth = login.iAuth));

    store
      .pipe(
        select(selectorCoreSelectedInstance),
        filter(selectedInstance => !!selectedInstance)
      )
      .subscribe(selectedInstance => (this.instanceUrl = selectedInstance.url));
  }

  /**
   * Fait une requête DELETE
   *
   * @param url URL
   * @param options Options
   */
  delete<T>(url: string, options: CoreHttpRequestOptions = {}): Observable<T> {
    return this.forgeObservable<T>(
      this.http.delete<T>(this.forgeUrl(url), this.forgeOptions(options))
    );
  }

  /**
   * Fait une requête GET
   *
   * @param url URL
   * @param options Options
   */
  get<T>(url: string, options: CoreHttpRequestOptions = {}): Observable<T> {
    return this.forgeObservable<T>(
      this.http.get<T>(this.forgeUrl(url), this.forgeOptions(options))
    );
  }

  /**
   * Fait une requête HEAD
   *
   * @param url URL
   * @param options Options
   */
  head<T>(url: string, options: CoreHttpRequestOptions = {}): Observable<T> {
    return this.forgeObservable<T>(
      this.http.head<T>(this.forgeUrl(url), this.forgeOptions(options))
    );
  }

  /**
   * Fait une requête OPTIONS
   *
   * @param url URL
   * @param options Options
   */
  options<T>(url: string, options: CoreHttpRequestOptions = {}): Observable<T> {
    return this.forgeObservable<T>(
      this.http.options<T>(this.forgeUrl(url), this.forgeOptions(options))
    );
  }

  /**
   * Fait une requête PATCH
   *
   * @param url URL
   * @param body Body
   * @param options Options
   */
  patch<T>(
    url: string,
    body: any | null,
    options: CoreHttpRequestOptions = {}
  ): Observable<T> {
    return this.forgeObservable<T>(
      this.http.patch<T>(this.forgeUrl(url), body, this.forgeOptions(options))
    );
  }

  /**
   * Fait une requête POST
   *
   * @param url URL
   * @param body Body
   * @param options Options
   */
  post<T>(
    url: string,
    body: any | null,
    options: CoreHttpRequestOptions = {}
  ): Observable<T> {
    return this.forgeObservable<T>(
      this.http.post<T>(this.forgeUrl(url), body, this.forgeOptions(options))
    );
  }

  /**
   * Fait une requête PUT
   *
   * @param url URL
   * @param body Body
   * @param options Options
   */
  put<T>(
    url: string,
    body: any | null,
    options: CoreHttpRequestOptions = {}
  ): Observable<T> {
    return this.forgeObservable<T>(
      this.http.put<T>(this.forgeUrl(url), body, this.forgeOptions(options))
    );
  }

  /**
   * Télécharge un Blob
   *
   * @param url URL
   * @param options Options
   */
  downloadBlob(
    url: string,
    options: CoreHttpRequestOptions = {}
  ): Observable<HttpResponse<Blob>> {
    return this.forgeObservable(
      this.http.get(this.forgeUrl(url), {
        ...this.forgeOptions(options),
        responseType: 'blob',
        observe: 'response'
      })
    );
  }

  downloadedBlobOperator(): OperatorFunction<
    HttpResponse<Blob>,
    { blob: Blob; filename: string }
  > {
    return map(res => {
      const exec = /filename="(.+)"/.exec(
        (res.headers && res.headers.get('Content-Disposition')) || ''
      );

      return {
        blob: res.body || new Blob(),
        filename: (exec && exec.length && exec[1]) || 'unknown'
      };
    });
  }

  /**
   * @ignore
   */
  private forgeUrl(endpoint: string): string {
    return /^(https?:\/\/)|(\.\/)/i.test(endpoint)
      ? endpoint
      : this.instanceUrl + endpoint;
  }

  /**
   * @ignore
   */
  private forgeOptions(
    options: CoreHttpRequestOptions
  ): CoreHttpRequestOptions {
    let headers: HttpHeaders;
    if (options.headers instanceof HttpHeaders) {
      headers = options.headers;
    } else {
      const instanceUrl = localStorage.getItem('instanceUrl');

      if (instanceUrl) {
        headers = new HttpHeaders(options.headers);
        headers = headers.append('Instance-URL', instanceUrl);
      } else {
        headers = new HttpHeaders(options.headers);
      }
    }
    if (options.withMobiCredentials) {
      headers = headers.append('Authorization', `basic ${this.iAuth}`);
    }

    return { ...options, headers: headers };
  }

  /**
   * @ignore
   */
  private forgeObservable<T>(requestObservable: Observable<T>): Observable<T> {
    return of(true).pipe(
      tap(() => this.store.dispatch(new CoreHttpActionIncrementPendingQueue())),
      switchMap(() =>
        navigator.onLine
          ? requestObservable
          : throwError(
              new HttpErrorResponse({
                error: { error: { msg: 'bepatient http error' } },
                statusText: BepatientHttpStatusTextsError.NO_NETWORK
              })
            )
      ),
      catchError((error: HttpErrorResponse) => {
        const coreHttpError: CoreHttpError =
          error.error instanceof ErrorEvent
            ? { message: error.error.message }
            : {
                message:
                  (error.error.message ? error.error.message : false) ||
                  (error.error.msg ? error.error.msg : false) ||
                  (error.error.error && error.error.error.msg),
                name: error.name,
                status: error.status,
                statusText: error.error.msg || error.statusText
              };
        this.store.dispatch(new CoreHttpActionSetError(coreHttpError));
        if (coreHttpError.status == 401) {
          // If iAuth is present but got 401
          this.store
            .pipe(select(selectorCoreLogin))
            .subscribe(coreLogin => {
              if (coreLogin && coreLogin.iAuth) {
                this.store.dispatch(new CoreActionJwtHasExpired());
              }
            })
            .unsubscribe();
          // If instance info is not present got 401
          this.store
            .pipe(select(selectorCoreInstanceInfo))
            .subscribe(instanceInfo => {
              if (!instanceInfo) {
                this.store.dispatch(new CoreActionJwtHasExpired());
              }
            })
            .unsubscribe();
        } else if (
          coreHttpError.status === 0 ||
          coreHttpError.statusText === BepatientHttpStatusTextsError.NO_NETWORK
        ) {
          navigator.onLine
            ? this.store.dispatch(new CoreHttpActionApiResponseError())
            : this.store.dispatch(new CoreHttpActionInternetIsNotWorking());
        } else if (coreHttpError.status === 404) {
          this.store
            .pipe(
              withLatestFrom(
                this.store.select(selectorCoreLogin),
                this.store.select(selectorCoreInstancesData),
                this.store.select(selectorCoreInstanceEnv)
              ),
              map(([, coreLogin, instancesData, instanceEnv]) => {
                if (!coreLogin && instancesData) {
                  if (instancesData[Object.keys(instancesData)[0]].code) {
                    const core = {
                      instanceEnv: instanceEnv
                    };
                    localStorage.setItem('core', JSON.stringify(core));
                  }
                }
              })
            )
            .subscribe();
        } else if (coreHttpError.status === 500) {
          this.store.dispatch(new CoreHttpActionResponseInternalError());
        }
        return throwError(error);
      }),
      finalize(() =>
        this.store.dispatch(new CoreHttpActionDecrementPendingQueue())
      )
    );
  }
}
